// HFLedger/ReportAnalytics.js

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import './ReportAnalytics.css';

function ReportAnalytics({ user, language, SERVER_ROOT, settings }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [monthlySummary, setMonthlySummary] = useState([]);
  const [categoryExpenses, setCategoryExpenses] = useState([]);
  const [dateRange, setDateRange] = useState({
    startDate: new Date(new Date().getFullYear(), new Date().getMonth() - 5, 1).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0]
  });
  const [reportType, setReportType] = useState('monthly');

  // 색상 팔레트
  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#A28BFF', '#FF6B6B', '#4ECDC4', '#C7F464', '#81D8D0', '#FFC300'];

  // 데이터 불러오기
  useEffect(() => {
    if (!user) return;
    
    const fetchReportData = async () => {
      setLoading(true);
      try {
        // 1. 월별 수입/지출 요약
        const monthlySummaryResponse = await axios.get(
          `${SERVER_ROOT}/api/hfl/transactions/summary/monthly?user_id=${user.uid}`
        );
        
        // 최근 6개월 데이터만 가져와서 정렬
        const last6Months = monthlySummaryResponse.data
          .sort((a, b) => {
            if (a.year !== b.year) return a.year - b.year;
            return a.month - b.month;
          })
          .slice(-6);
          
        setMonthlySummary(last6Months);
        
        // 2. 카테고리별 지출 분석
        const categoryExpensesResponse = await axios.get(
          `${SERVER_ROOT}/api/hfl/transactions/summary/category?user_id=${user.uid}&start_date=${dateRange.startDate}&end_date=${dateRange.endDate}&type=expense`
        );
        
        setCategoryExpenses(categoryExpensesResponse.data);
        
      } catch (err) {
        console.error('Error fetching report data:', err);
        setError(err.message || '보고서 데이터를 불러오는 중 오류가 발생했습니다.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchReportData();
  }, [user, SERVER_ROOT, dateRange]);

  // 날짜 범위 변경 핸들러
  const handleDateRangeChange = (e) => {
    const { name, value } = e.target;
    setDateRange(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // 리포트 타입 변경 핸들러
  const handleReportTypeChange = (type) => {
    setReportType(type);
  };

  // 월 이름 포맷팅
  const formatMonth = (year, month) => {
    const date = new Date(year, month - 1);
    return new Intl.DateTimeFormat(language === 'KR' ? 'ko-KR' : 'en-US', {
      year: 'numeric',
      month: 'short'
    }).format(date);
  };

  // 통화 포맷팅
  const formatCurrency = (amount, currency = settings.default_currency) => {
    return new Intl.NumberFormat(language === 'KR' ? 'ko-KR' : 'en-US', {
      style: 'currency',
      currency: currency,
      maximumFractionDigits: 0
    }).format(amount);
  };

  // 차트 데이터 생성 - 월별 추이
  const createMonthlyTrendData = () => {
    return monthlySummary.map(item => {
      // 해당 월의 기본 통화 금액만 사용
      const income = item.currencies[settings.default_currency]?.income || 0;
      const expense = item.currencies[settings.default_currency]?.expense || 0;
      
      return {
        name: formatMonth(item.year, item.month),
        income: income,
        expense: expense,
        balance: income - expense
      };
    });
  };

  // 차트 데이터 생성 - 카테고리별 지출
  const createCategoryExpenseData = () => {
    // 같은 통화의 카테고리만 그룹화
    const filteredData = categoryExpenses
      .filter(item => item.currency === settings.default_currency)
      .sort((a, b) => b.total - a.total);
    
    // 상위 카테고리만 표시하고 나머지는 '기타'로 묶기
    const topCategories = filteredData.slice(0, 5);
    
    const otherTotal = filteredData
      .slice(5)
      .reduce((sum, item) => sum + parseFloat(item.total), 0);
    
    if (otherTotal > 0) {
      topCategories.push({
        category_name: language === 'KR' ? '기타' : 'Others',
        total: otherTotal,
        currency: settings.default_currency
      });
    }
    
    return topCategories.map(item => ({
      name: item.category_name,
      value: parseFloat(item.total)
    }));
  };

  // 차트 툴팁 커스텀
  const CustomTooltip = ({ active, payload, label, isMonthly }) => {
    if (active && payload && payload.length) {
      return (
        <div className="custom-tooltip">
          <p className="tooltip-label">{label}</p>
          {payload.map((entry, index) => (
            <p key={index} style={{ color: entry.color }}>
              {isMonthly ? (entry.name === 'balance' 
                ? (language === 'KR' ? '잔액' : 'Balance')
                : entry.name === 'income'
                  ? (language === 'KR' ? '수입' : 'Income')
                  : (language === 'KR' ? '지출' : 'Expense')) : entry.name}: {formatCurrency(entry.value)}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  // 월별 비교 컴포넌트
  const MonthlyComparison = () => {
    const data = createMonthlyTrendData();
    
    return (
      <div className="report-chart-container">
        <h3>{language === 'KR' ? '월별 수입/지출 비교' : 'Monthly Income & Expense'}</h3>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart
            data={data}
            margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" />
            <YAxis />
            <Tooltip content={<CustomTooltip isMonthly={true} />} />
            <Legend />
            <Bar dataKey="income" name={language === 'KR' ? '수입' : 'Income'} fill="#4CAF50" />
            <Bar dataKey="expense" name={language === 'KR' ? '지출' : 'Expense'} fill="#F44336" />
          </BarChart>
        </ResponsiveContainer>
      </div>
    );
  };

  // 추세 분석 컴포넌트
  const TrendAnalysis = () => {
    const data = createMonthlyTrendData();
    
    return (
      <div className="report-chart-container">
        <h3>{language === 'KR' ? '월별 재정 추세' : 'Monthly Financial Trend'}</h3>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart
            data={data}
            margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" />
            <YAxis />
            <Tooltip content={<CustomTooltip isMonthly={true} />} />
            <Legend />
            <Line 
              type="monotone" 
              dataKey="income" 
              name={language === 'KR' ? '수입' : 'Income'} 
              stroke="#4CAF50" 
              activeDot={{ r: 8 }} 
            />
            <Line 
              type="monotone" 
              dataKey="expense" 
              name={language === 'KR' ? '지출' : 'Expense'} 
              stroke="#F44336" 
            />
            <Line 
              type="monotone" 
              dataKey="balance" 
              name={language === 'KR' ? '잔액' : 'Balance'} 
              stroke="#2196F3" 
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    );
  };

  // 카테고리별 지출 컴포넌트
  const CategoryExpenseAnalysis = () => {
    const data = createCategoryExpenseData();
    
    // 총 금액 계산
    const total = data.reduce((sum, item) => sum + item.value, 0);
    
    const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, index, name }) => {
      const RADIAN = Math.PI / 180;
      const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
      const x = cx + radius * Math.cos(-midAngle * RADIAN);
      const y = cy + radius * Math.sin(-midAngle * RADIAN);
      
      return (
        <text 
          x={x} 
          y={y} 
          fill="white" 
          textAnchor={x > cx ? 'start' : 'end'} 
          dominantBaseline="central"
          fontSize={12}
        >
          {`${(percent * 100).toFixed(0)}%`}
        </text>
      );
    };
    
    return (
      <div className="report-chart-container">
        <h3>{language === 'KR' ? '카테고리별 지출 분석' : 'Expense by Category'}</h3>
        {/* <div className="pie-chart-container"> */}
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={data}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={renderCustomizedLabel}
                outerRadius={100}
                fill="#8884d8"
                dataKey="value"
              >
                {data.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip 
                formatter={(value) => formatCurrency(value)}
                content={<CustomTooltip />} 
              />
            </PieChart>
          </ResponsiveContainer>
          
          <div className="category-legend">
            {data.map((entry, index) => (
              <div key={`legend-${index}`} className="category-legend-item">
                <div 
                  className="color-box" 
                  style={{ backgroundColor: COLORS[index % COLORS.length] }}
                ></div>
                <span className="category-name">{entry.name}</span>
                <span className="category-value">
                  {formatCurrency(entry.value)} ({((entry.value / total) * 100).toFixed(1)}%)
                </span>
              </div>
            ))}
          </div>
        {/* </div> */}
      </div>
    );
  };

  // 요약 통계 컴포넌트
  const SummaryStatistics = () => {
    // 통계 계산
    const monthlyData = createMonthlyTrendData();
    const categoryData = createCategoryExpenseData();
    
    // 최근 월 데이터
    const latestMonth = monthlyData[monthlyData.length - 1] || { income: 0, expense: 0, balance: 0 };
    
    // 전월 대비 변화율 계산
    const previousMonth = monthlyData[monthlyData.length - 2] || { income: 0, expense: 0, balance: 0 };
    const incomeChange = previousMonth.income ? ((latestMonth.income - previousMonth.income) / previousMonth.income) * 100 : 0;
    const expenseChange = previousMonth.expense ? ((latestMonth.expense - previousMonth.expense) / previousMonth.expense) * 100 : 0;
    
    // 최대 지출 카테고리
    const topCategory = categoryData[0] || { name: '-', value: 0 };
    
    return (
      <div className="summary-statistics">
        <h3>{language === 'KR' ? '요약 통계' : 'Summary Statistics'}</h3>
        <div className="stats-grid">
          <div className="stat-card">
            <h4>{language === 'KR' ? '이번 달 수입' : 'This Month Income'}</h4>
            <p className="stat-value income">{formatCurrency(latestMonth.income)}</p>
            <p className={`stat-change ${incomeChange >= 0 ? 'positive' : 'negative'}`}>
              {incomeChange >= 0 ? '▲' : '▼'} {Math.abs(incomeChange).toFixed(1)}%
            </p>
          </div>
          
          <div className="stat-card">
            <h4>{language === 'KR' ? '이번 달 지출' : 'This Month Expense'}</h4>
            <p className="stat-value expense">{formatCurrency(latestMonth.expense)}</p>
            <p className={`stat-change ${expenseChange <= 0 ? 'positive' : 'negative'}`}>
              {expenseChange <= 0 ? '▼' : '▲'} {Math.abs(expenseChange).toFixed(1)}%
            </p>
          </div>
          
          <div className="stat-card">
            <h4>{language === 'KR' ? '최대 지출 카테고리' : 'Top Expense Category'}</h4>
            <p className="stat-value">{topCategory.name}</p>
            <p className="stat-subvalue">{formatCurrency(topCategory.value)}</p>
          </div>
          
          <div className="stat-card">
            <h4>{language === 'KR' ? '월간 저축' : 'Monthly Savings'}</h4>
            <p className="stat-value balance">{formatCurrency(latestMonth.balance)}</p>
            <p className="stat-subvalue">
              {language === 'KR' 
                ? `수입의 ${Math.round((latestMonth.balance / latestMonth.income) * 100)}%` 
                : `${Math.round((latestMonth.balance / latestMonth.income) * 100)}% of income`}
            </p>
          </div>
        </div>
      </div>
    );
  };

  if (loading && monthlySummary.length === 0) {
    return (
      <div className="reports-loading">
        <div className="reports-spinner"></div>
        <p>{language === 'KR' ? '보고서 데이터 로딩 중...' : 'Loading report data...'}</p>
      </div>
    );
  }

  if (error && monthlySummary.length === 0) {
    return (
      <div className="reports-error">
        <h3>{language === 'KR' ? '데이터 로딩 오류' : 'Error Loading Data'}</h3>
        <p>{error}</p>
        <button 
          onClick={() => window.location.reload()}
          className="reports-button"
        >
          {language === 'KR' ? '새로고침' : 'Refresh'}
        </button>
      </div>
    );
  }

  return (
    <div className="reports-container">
      <div className="reports-header">
        <h2>{language === 'KR' ? '재정 보고서 및 분석' : 'Financial Reports & Analytics'}</h2>
        
        <div className="reports-date-range">
          <div className="date-input-group">
            <label htmlFor="startDate">{language === 'KR' ? '시작일' : 'Start Date'}</label>
            <input 
              type="date" 
              id="startDate"
              name="startDate"
              value={dateRange.startDate}
              onChange={handleDateRangeChange}
            />
          </div>
          
          <div className="date-input-group">
            <label htmlFor="endDate">{language === 'KR' ? '종료일' : 'End Date'}</label>
            <input 
              type="date" 
              id="endDate"
              name="endDate"
              value={dateRange.endDate}
              onChange={handleDateRangeChange}
            />
          </div>
        </div>
      </div>
      
      <div className="reports-type-selector">
        <button 
          className={reportType === 'monthly' ? 'active' : ''}
          onClick={() => handleReportTypeChange('monthly')}
        >
          {language === 'KR' ? '월간 분석' : 'Monthly Analysis'}
        </button>
        <button 
          className={reportType === 'category' ? 'active' : ''}
          onClick={() => handleReportTypeChange('category')}
        >
          {language === 'KR' ? '카테고리 분석' : 'Category Analysis'}
        </button>
        <button 
          className={reportType === 'trend' ? 'active' : ''}
          onClick={() => handleReportTypeChange('trend')}
        >
          {language === 'KR' ? '추세 분석' : 'Trend Analysis'}
        </button>
        <button 
          className={reportType === 'summary' ? 'active' : ''}
          onClick={() => handleReportTypeChange('summary')}
        >
          {language === 'KR' ? '요약 통계' : 'Summary Stats'}
        </button>
      </div>
      
      <div className="reports-content">
        {reportType === 'monthly' && <MonthlyComparison />}
        {reportType === 'category' && <CategoryExpenseAnalysis />}
        {reportType === 'trend' && <TrendAnalysis />}
        {reportType === 'summary' && <SummaryStatistics />}
      </div>
      
      <div className="reports-notes">
        <p className="note">
          <i className="fas fa-info-circle"></i>
          {language === 'KR' 
            ? '참고: 모든 금액은 기본 통화인 ' + settings.default_currency + '로 표시됩니다.'
            : 'Note: All amounts are shown in your default currency, ' + settings.default_currency + '.'}
        </p>
      </div>
    </div>
  );
}

export default ReportAnalytics;